import React, { useRef, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { ESACacheMessage } from '../../cache/MessageCache';

export interface VirtualizedMessageListProps {
  messages: ESACacheMessage[];
  onLoadMore: (beforeMessageId: string) => Promise<void>;
  renderMessage: (message: ESACacheMessage, index: number) => React.ReactNode;
  /**
   * Optional content rendered after all virtualised message rows, still inside
   * the scrollable container. Use to inject contextual cards (e.g. SolutionRunCard)
   * that should appear as the last item in the conversation thread.
   */
  trailingContent?: React.ReactNode;
  /**
   * When this value changes, scroll to the latest row after layout. Used when
   * an external footer (e.g. ConverseStatusCard) appears/disappears and shrinks
   * or restores the list viewport so the last message stays visible.
   */
  scrollToBottomKey?: string | number | boolean;
  className?: string;
}

export function VirtualizedMessageList({
  messages,
  onLoadMore,
  renderMessage,
  trailingContent,
  scrollToBottomKey,
  className,
}: VirtualizedMessageListProps): React.ReactElement {
  const parentRef = useRef<HTMLDivElement>(null);
  const isLoadingMore = useRef(false);
  const prevMessageCount = useRef(0);
  // Track whether we've performed the initial scroll-to-bottom for this thread.
  const initialScrollDone = useRef(false);
  // Id of the last message seen — lets us tell an append (new message at the
  // bottom) apart from a prepend (older history loaded via scroll-up pagination).
  const prevLastMessageId = useRef<string | null>(null);
  const prevScrollToBottomKey = useRef<string | number | boolean | undefined>(scrollToBottomKey);

  // Inject trailingContent as a real virtual item so the virtualizer owns its
  // height measurement and scroll extent. Placing it outside the virtualizer
  // container (after the height:getTotalSize div) causes it to clip or float
  // against the viewport edge due to contain:strict size containment.
  const hasTrailing = Boolean(trailingContent);
  const totalCount = messages.length + (hasTrailing ? 1 : 0);

  const virtualizer = useVirtualizer({
    count: totalCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 80,
    overscan: 5,
  });

  useEffect(() => {
    if (totalCount === 0) return;

    const wasEmpty = prevMessageCount.current === 0;
    const grew = totalCount > prevMessageCount.current;
    prevMessageCount.current = totalCount;

    // Determine whether the newest message changed (append at the bottom) vs an
    // older-history prepend, which grows the count but leaves the tail unchanged.
    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : undefined;
    const lastMessageId = lastMessage?.id ?? null;
    const appended = lastMessageId !== prevLastMessageId.current;
    prevLastMessageId.current = lastMessageId;

    // Initial load: cache went 0 → N (reload or switching threads).
    // Use rAF to let the virtualizer lay out all rows before scrolling.
    if (wasEmpty && !initialScrollDone.current) {
      initialScrollDone.current = true;
      requestAnimationFrame(() => {
        virtualizer.scrollToIndex(totalCount - 1, { align: 'end' });
      });
      return;
    }

    if (grew) {
      // The user's own outgoing message must always snap the view back to the
      // bottom, even if they had scrolled all the way up to read history.
      if (appended && lastMessage?.initiator === 'user') {
        virtualizer.scrollToIndex(totalCount - 1, { align: 'end' });
        return;
      }

      // Otherwise (incoming agent message) only follow if already near bottom,
      // so a user reading older messages isn't yanked down mid-conversation.
      const scrollEl = parentRef.current;
      if (scrollEl) {
        const distanceFromBottom = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
        if (distanceFromBottom < 200) {
          virtualizer.scrollToIndex(totalCount - 1, { align: 'end' });
        }
      }
    }
  }, [totalCount, messages, virtualizer]);

  // External footer (status flash card) resized the list viewport — pin to bottom
  // so the last message is not clipped behind the card (or left short when it goes).
  useEffect(() => {
    if (scrollToBottomKey === undefined) return;
    if (prevScrollToBottomKey.current === scrollToBottomKey) return;
    prevScrollToBottomKey.current = scrollToBottomKey;
    if (totalCount === 0) return;
    // Double rAF: wait for flex layout after the footer card mounts/unmounts.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        virtualizer.scrollToIndex(totalCount - 1, { align: 'end' });
      });
    });
  }, [scrollToBottomKey, totalCount, virtualizer]);

  const handleScroll = React.useCallback(async (): Promise<void> => {
    const el = parentRef.current;
    if (!el || isLoadingMore.current || messages.length === 0) return;
    if (el.scrollTop < 100) {
      isLoadingMore.current = true;
      try {
        await onLoadMore(messages[0].id);
      } finally {
        isLoadingMore.current = false;
      }
    }
  }, [messages, onLoadMore]);

  return (
    <div
      ref={parentRef}
      /*
       * h-full is required: contain:strict needs a definite dimension to size
       * the layout box. flex-1 alone does NOT give a definite height when the
       * parent is a block element — the browser collapses it to 0px, making
       * the virtualizer think no items fit in the viewport.
       * The caller must give this component a parent with a definite height
       * (e.g. `flex-1 min-h-0 overflow-hidden`).
       */
      className={`h-full overflow-y-auto ${className ?? ''}`}
      onScroll={() => void handleScroll()}
      style={{ contain: 'strict' }}
    >
      <div
        className="relative w-full"
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualizer.getVirtualItems().map(virtualRow => (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            ref={virtualizer.measureElement}
            className="absolute top-0 left-0 w-full"
            style={{ transform: `translateY(${virtualRow.start}px)` }}
          >
            {virtualRow.index < messages.length
              ? renderMessage(messages[virtualRow.index], virtualRow.index)
              : trailingContent
            }
          </div>
        ))}
      </div>
    </div>
  );
}
